import { useState, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Typography,
  Switch,
  FormControlLabel,
  Snackbar,
} from '@material-ui/core';
import PublishIcon from '@material-ui/icons/Publish';
import RefreshIcon from '@material-ui/icons/Refresh';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { makeStyles } from '@material-ui/core/styles';
import { InfoCard, Link } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef, gitlabAuthApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/lib/useAsync';

// ── Annotation used to link a catalog entity to its MLflow experiment ────────
const MLFLOW_EXPERIMENT_ANNOTATION = 'code4ev.io/mlflow-experiment-id';
const MLFLOW_AUTO_COMMIT_ANNOTATION = 'code4ev.io/mlflow-auto-commit';

/** MLflow run status → MUI chip colour */
const statusColour = (status: string): 'default' | 'primary' | 'secondary' => {
  switch (status) {
    case 'FINISHED':
      return 'primary';
    case 'FAILED':
    case 'KILLED':
      return 'secondary';
    default:
      return 'default';
  }
};

const useStyles = makeStyles(theme => ({
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  metricCell: {
    fontFamily: 'monospace',
    fontSize: '12px',
    color: theme.palette.text.secondary,
  },
  emptyText: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}));

// ── Types ────────────────────────────────────────────────────────────────────
interface MlflowRun {
  info: {
    run_id: string;
    status: string;
    start_time: number;
    end_time?: number;
    lifecycle_stage: string;
  };
  data: {
    metrics?: Array<{ key: string; value: number }>;
    params?: Array<{ key: string; value: string }>;
    tags?: Array<{ key: string; value: string }>;
  };
}

interface MlflowExperiment {
  experiment_id: string;
  name: string;
}

// ── Component ────────────────────────────────────────────────────────────────
export const MlflowExperimentCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const gitlabAuth = useApi(gitlabAuthApiRef);

  const experimentName =
    entity.metadata.annotations?.[MLFLOW_EXPERIMENT_ANNOTATION] ??
    entity.metadata.name;

  const defaultAutoCommit =
    entity.metadata.annotations?.[MLFLOW_AUTO_COMMIT_ANNOTATION] === 'true';

  const [autoCommit, setAutoCommit] = useState(defaultAutoCommit);
  const [committingRunId, setCommittingRunId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // ── Fetch experiment + runs via Backstage proxy ───────────────────────────
  const { value: runs, loading, error } = useAsync(async () => {
    const baseUrl = await discoveryApi.getBaseUrl('proxy');
    const mlflowBase = `${baseUrl}/mlflow/api/2.0/mlflow`;

    // Look up experiment by name
    const expRes = await fetchApi.fetch(
      `${mlflowBase}/experiments/get-by-name?experiment_name=${encodeURIComponent(experimentName)}`,
    );
    if (!expRes.ok) {
      if (expRes.status === 404) return [];
      throw new Error(`MLflow experiment fetch failed: ${expRes.statusText}`);
    }
    const expJson = await expRes.json();
    const experiment: MlflowExperiment = expJson.experiment;

    // Fetch latest 10 runs
    const runsRes = await fetchApi.fetch(`${mlflowBase}/runs/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        experiment_ids: [experiment.experiment_id],
        max_results: 10,
        order_by: ['start_time DESC'],
      }),
    });
    if (!runsRes.ok) throw new Error(`MLflow runs fetch failed: ${runsRes.statusText}`);
    const runsJson = await runsRes.json();
    return (runsJson.runs ?? []) as MlflowRun[];
  }, [experimentName, refreshTick]);

  // ── Commit a run's model artifact to the GitLab ai-models-* repo ─────────
  const commitToArtifactRepo = useCallback(
    async (runId: string) => {
      setCommittingRunId(runId);
      try {
        // Acquire the user's own GitLab OAuth token so the GitLab commit is
        // attributed to this individual rather than the service account.
        const userGitlabToken = await gitlabAuth.getAccessToken(['api', 'write_repository']);
        const baseUrl = await discoveryApi.getBaseUrl('mlflow-bridge');
        const res = await fetchApi.fetch(`${baseUrl}/commit-model`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Gitlab-Token': userGitlabToken,
          },
          body: JSON.stringify({
            runId,
            entityName: entity.metadata.name,
            autoVersionTag: true,
          }),
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error ?? res.statusText);
        }
        const result = await res.json();
        setSnackbar(`✓ Committed as version ${result.version} (${result.commitSha?.slice(0, 8)})`);
      } catch (err: any) {
        setSnackbar(`✗ Commit failed: ${err.message}`);
      } finally {
        setCommittingRunId(null);
      }
    },
    [discoveryApi, fetchApi, entity.metadata.name],
  );

  const mlflowRunUrl = (runId: string) =>
    `/api/proxy/mlflow/#/experiments/-/runs/${runId}`;

  const formatTime = (ts?: number) =>
    ts ? new Date(ts).toLocaleString() : '—';

  const topMetric = (run: MlflowRun): string => {
    const metrics = run.data?.metrics ?? [];
    if (!metrics.length) return '—';
    // Show the last recorded metric (typically the primary eval metric)
    const m = metrics[metrics.length - 1];
    return `${m.key}: ${typeof m.value === 'number' ? m.value.toFixed(4) : m.value}`;
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const headerControls = (
    <div className={classes.headerRow}>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={autoCommit}
            onChange={e => setAutoCommit(e.target.checked)}
            color="primary"
          />
        }
        label={<Typography variant="caption">Auto-commit</Typography>}
      />
      <Tooltip title="Refresh runs">
        <IconButton size="small" onClick={() => setRefreshTick(t => t + 1)}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  let body: React.ReactNode;

  if (loading) {
    body = (
      <div className={classes.loadingWrapper}>
        <CircularProgress size={28} />
      </div>
    );
  } else if (error) {
    body = (
      <Typography className={classes.emptyText}>
        {error.message.includes('404')
          ? `No MLflow experiment found for "${experimentName}". Create one or add the annotation code4ev.io/mlflow-experiment-id to this entity.`
          : `Error: ${error.message}`}
      </Typography>
    );
  } else if (!runs || runs.length === 0) {
    body = (
      <Typography className={classes.emptyText}>
        No training runs yet. Start a run from your training pipeline using experiment name{' '}
        <strong>{experimentName}</strong>.
      </Typography>
    );
  } else {
    body = (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Run ID</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Started</TableCell>
            <TableCell>Top Metric</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {runs.map(run => (
            <TableRow key={run.info.run_id} hover>
              <TableCell>
                <Link to={mlflowRunUrl(run.info.run_id)}>
                  {run.info.run_id.slice(0, 8)}…
                </Link>
              </TableCell>
              <TableCell>
                <Chip
                  label={run.info.status}
                  size="small"
                  color={statusColour(run.info.status)}
                />
              </TableCell>
              <TableCell>{formatTime(run.info.start_time)}</TableCell>
              <TableCell className={classes.metricCell}>{topMetric(run)}</TableCell>
              <TableCell align="right">
                <Tooltip title="View full run in MLflow">
                  <IconButton
                    size="small"
                    href={mlflowRunUrl(run.info.run_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {run.info.status === 'FINISHED' && (
                  <Tooltip title="Commit model artifact to GitLab Artifact Repository">
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={committingRunId === run.info.run_id}
                        onClick={() => commitToArtifactRepo(run.info.run_id)}
                      >
                        {committingRunId === run.info.run_id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <PublishIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <>
      <InfoCard
        title="MLflow Experiment Runs"
        subheader={`Experiment: ${experimentName}`}
        action={headerControls}
        noPadding
      >
        {body}
      </InfoCard>
      <Snackbar
        open={!!snackbar}
        message={snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
      />
    </>
  );
};
